import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, comicCategories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const filteredComics = useMemo(() => {
    let result: Comic[] = activeCategory === 'all'
      ? [...comics]
      : comics.filter((c) => c.category === activeCategory);

    switch (sortBy) {
      case 'latest':
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
      default:
        result.sort((a, b) => b.views - a.views);
        break;
    }
    return result;
  }, [activeCategory, sortBy]);

  const currentCategory = comicCategories.find((c) => c.slug === activeCategory);
  const categoryName = currentCategory ? currentCategory.name : '全部';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${categoryName}韩国漫画 - 韩漫网站`,
    'description': `在韩漫网站免费在线阅读${categoryName}类型的韩国漫画，下拉式韩漫体验，漫画大全持续更新。`,
    'url': `https://www.hanmanwangzhan.com/category/${activeCategory}`,
    'inLanguage': 'zh-CN',
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩漫网站',
      'url': 'https://www.hanmanwangzhan.com/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: `${categoryName}韩国漫画` },
              ]}
            />

            <div className="mt-4">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">
                {categoryName}韩国漫画
              </h1>
              <p className="text-foreground-500 mt-2 text-sm">
                {activeCategory === 'all'
                  ? '浏览全部韩国漫画资源，发现更多好看的漫画大全，免费下拉式在线阅读'
                  : `免费在线阅读${categoryName}类型的韩国漫画，下拉式韩漫体验，发现您喜爱的作品`
                }
              </p>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {comicCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={cat.icon}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Sort Controls */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共找到 <strong className="text-foreground-900">{filteredComics.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
              {[
                { key: 'popular' as const, label: '最热门' },
                { key: 'latest' as const, label: '最新' },
                { key: 'rating' as const, label: '最高评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Comic Grid */}
        <section className="pb-14 md:pb-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 mx-auto flex items-center justify-center text-foreground-300">
                  <i className="ri-emotion-sad-line text-4xl"></i>
                </span>
                <p className="text-foreground-500 mt-4">该分类暂无作品，请查看其他分类</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 px-6 py-2 bg-primary-500 text-white text-sm rounded-full hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  查看全部漫画
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}